import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Container, Form, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faArrowRight, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';

import { ADVANCED_MAPPING_FUNCTIONS } from 'lib/constants';
import AdvancedAtttributeMaooingModalAttributeValueSection from 'components/data_sources/single_data_source_view/AdvancedAtttributeMappingModalAttributeValueSection';
import { ATTRIBUTE, ATTRIBUTE_VALUES, GLOBAL_ATTRIBUTE } from 'lib/networking/endpoints';
import { neutralGray } from 'lib/css/colors';
import isAdvancedMapping from 'lib/utils/isAdvancedMapping';
import pointerOnHover from 'lib/css/pointerOnHover';
import { areYouSure } from 'lib/utils/toast';
import useGet from 'lib/hooks/useGet';
import AttributeInputType from 'lib/enums/AttributeInputType';
function AdvancedAttributeMappingModal({
  onHide,
  attributes,
  attributeMap,
  unsavedAttributeMap,
  setUnsavedAttributeMap,
  attributesToFilter,
  editingAttribute,
  editingValue,
}) {
  const [sourceAttribute, setSourceAttribute] = useState('');
  const [selectedOperation, setSelectedOperation] = useState('');
  const [newAdvancedMapping, setNewAdvancedMapping] = useState({});
  const [removeFlags, setRemoveFlags] = useState({});
  const [attribute_id, setAttributeID] = useState();
  const [global_attribute_id, setGlobalAttributeID] = useState();
  const [displayedAttribute, setDisplayedAttribute] = useState();
  const [originalAttribute, setOriginalAttribute] = useState(null);

  const { data: { attribute } = {} } = useGet(ATTRIBUTE, { attribute_id: attribute_id });

  const { data: { global_attribute } = {} } = useGet(
    GLOBAL_ATTRIBUTE,
    { global_attribute_id: global_attribute_id },
    !global_attribute_id,
  );

  const { data: { attribute_values } = {} } = useGet(
    ATTRIBUTE_VALUES,
    {
      ...(attribute_id && { attribute_id: attribute_id }),
      ...(global_attribute_id && { global_attribute_id: global_attribute_id }),
    },
    !attribute_id && !global_attribute_id,
  );

  const sourceAttributeOptions = Object.keys(attributeMap)
    .filter(attribute => !isAdvancedMapping(attribute))
    .map(attribute => ({
      value: attribute,
      label: attribute,
    }));

  const attributeOptions = attributes
    ?.filter(attr => !attributesToFilter.includes(attr.name))
    .map(attr => ({
      label: attr.name, // the name displayed in the dropdown
      value: {
        attribute_id: attr.attribute_id,
        global_attribute_id: attr.global_attribute_id,
      },
      inputType: attr.input_type,
    }));

  const operationOptions = Object.keys(ADVANCED_MAPPING_FUNCTIONS)
    .filter(operation => operation !== 'REMOVE_VALUE')
    .map(operation => ({
      value: operation,
      label: operation,
    }));

  const quickButtonsOperations = Object.keys(ADVANCED_MAPPING_FUNCTIONS).filter(
    operation => ADVANCED_MAPPING_FUNCTIONS[operation]?.isQuickAdd,
  );

  const isNewMappingsAreValid =
    Object.keys(newAdvancedMapping).length &&
    Object.keys(newAdvancedMapping).every(
      attribute => !attribute.includes('SOURCE_ATTRIBUTE') && newAdvancedMapping[attribute] !== '',
    );

  const handleAddOperation = useCallback(
    operation => {
      if (!sourceAttribute || (!selectedOperation && !operation)) {
        return;
      }
      if (!operation) {
        operation = selectedOperation;
      }
      const operationInsertValue = ADVANCED_MAPPING_FUNCTIONS[operation].insertValue;
      let newSourceAttribute = operationInsertValue.replace('SOURCE_ATTRIBUTE', sourceAttribute);
      const newAdvancedMappingKeys = Object.keys(newAdvancedMapping);
      if (newAdvancedMappingKeys.includes(newSourceAttribute)) {
        const existingKeys = newAdvancedMappingKeys.filter(key =>
          key.startsWith(newSourceAttribute),
        );
        const copyCount = existingKeys.length - 1;
        if (copyCount > 0) {
          // increment the _copy number
          newSourceAttribute = `${newSourceAttribute}_copy${copyCount + 1}`;
        } else {
          // if the newSourceAttribute already exists, add a _copy number
          newSourceAttribute = `${newSourceAttribute}_copy1`;
        }
      }
      setNewAdvancedMapping({
        ...newAdvancedMapping,
        [newSourceAttribute]: '',
      });
    },
    [newAdvancedMapping, selectedOperation, sourceAttribute],
  );
  useEffect(() => {
    // Initialize form when editing
    if (editingAttribute) {
      setOriginalAttribute(editingAttribute);
      // Unwrap REMOVE_VALUE(...) so the source field shows the base expression and
      // the "remove matched value" checkbox reflects the existing rule. originalAttribute
      // keeps the raw (wrapped) key so the save handler can find and replace it.
      const removeValueMatch = editingAttribute.match(/^REMOVE_VALUE\((.*)\)$/);
      const baseAttribute = removeValueMatch ? removeValueMatch[1] : editingAttribute;
      setNewAdvancedMapping({ [baseAttribute]: editingValue });
      if (removeValueMatch) {
        setRemoveFlags({ [baseAttribute]: true });
      }
    }
  }, [editingAttribute, editingValue]);

  useEffect(() => {
    if (selectedOperation && newAdvancedMapping && !Object.keys(newAdvancedMapping).length) {
      handleAddOperation();
    }
  }, [handleAddOperation, newAdvancedMapping, selectedOperation]);

  useEffect(() => {
    setDisplayedAttribute(attribute_id ? attribute : global_attribute);
  }, [attribute_id, global_attribute_id, attribute, global_attribute]);

  const handleChange = (option, attribute) => {
    setNewAdvancedMapping({
      ...newAdvancedMapping,
      [attribute]: option.label,
    });
    setAttributeID(option.value.attribute_id);
    setGlobalAttributeID(option.value.global_attribute_id);
  };
  return (
    <Modal size="xl" show={true} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Advanced Attribute Mapping</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row style={{ minHeight: '250px' }}>
          <Col>
            <Container>
              <Row>
                <Col>
                  <div className="font-weight-bold mb-2">1. Select source attribute</div>
                  <Select
                    className="w-75"
                    value={
                      sourceAttributeOptions.find(option => option.value === sourceAttribute) || ''
                    }
                    options={sourceAttributeOptions}
                    onChange={option => setSourceAttribute(option.value)}
                  />
                </Col>
              </Row>

              <Row className="mt-2">
                <Col>
                  <div className="font-weight-bold mb-2">2. Select operation</div>
                  <div className="d-flex">
                    <Select
                      isClearable
                      className="w-75"
                      value={
                        operationOptions.find(option => option.value === selectedOperation) || ''
                      }
                      options={operationOptions}
                      onChange={option => {
                        if (option === null) {
                          setSelectedOperation('');
                          return;
                        }
                        setSelectedOperation(option.value || '');
                      }}
                    />
                    <Button
                      className="ml-3"
                      variant="outline-primary"
                      disabled={!sourceAttribute || !selectedOperation}
                      onClick={() => handleAddOperation()}
                    >
                      + New Mapping
                    </Button>
                  </div>
                  <div>
                    {quickButtonsOperations.length > 0 &&
                      quickButtonsOperations.map((operation, index) => {
                        return (
                          <Button
                            key={index}
                            className="mt-2 mr-2"
                            variant="outline-primary"
                            disabled={!sourceAttribute}
                            onClick={() => handleAddOperation(operation)}
                          >
                            {operation}
                          </Button>
                        );
                      })}
                  </div>
                </Col>
              </Row>
              <div className="text-muted m-2">
                {selectedOperation && ADVANCED_MAPPING_FUNCTIONS[selectedOperation].description}
              </div>
            </Container>
          </Col>
          <Col>
            {attribute_values?.length > 0 && (
              <AdvancedAtttributeMaooingModalAttributeValueSection
                attribute_values={attribute_values}
                subSection={displayedAttribute?.attribute_sub_section || ''}
                section={displayedAttribute?.attribute_sub_section?.attribute_section || ''}
                attributeId={displayedAttribute?.attribute_id || ''}
              />
            )}
          </Col>
        </Row>

        <Container className="mt-3">
          <Row>
            <Col>
              {newAdvancedMapping &&
                Object.keys(newAdvancedMapping).map((attribute, index) => {
                  const isValidSourceAttribute = !attribute.includes('SOURCE_ATTRIBUTE');
                  return (
                    <div key={index} className={css(styles.mappingSection)}>
                      <Row>
                        <Col md={6} className="d-flex justify-content-center">
                          <Form.Label>Source Attribute</Form.Label>
                        </Col>
                        <Col md={1} className="d-flex justify-content-center"></Col>
                        <Col className="d-flex justify-content-center">
                          <Form.Label>FSA Attribute</Form.Label>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={attribute}
                            onChange={e => {
                              const newMapping = { ...newAdvancedMapping };
                              const newFlags = { ...removeFlags };
                              delete newMapping[attribute];
                              if (removeFlags[attribute]) {
                                newFlags[e.target.value] = removeFlags[attribute];
                                delete newFlags[attribute];
                              }
                              setNewAdvancedMapping({
                                ...newMapping,
                                [e.target.value]: newAdvancedMapping[attribute],
                              });
                              setRemoveFlags(newFlags);
                            }}
                            className={css(styles.smallText)}
                          />
                          <span className={css(styles.redText)}>
                            {!isValidSourceAttribute &&
                              sourceAttribute &&
                              'Invalid Source Attribute.Please replace SOURCE_ATTRIBUTE ' +
                                'with a valid attribute'}
                          </span>
                        </Col>
                        <Col md={1} className="d-flex justify-content-center align-items-center">
                          <FontAwesomeIcon icon={faArrowRight} />
                        </Col>
                        <Col className="d-flex justify-content-center align-items-center">
                          <Select
                            className="w-100"
                            options={attributeOptions}
                            value={
                              attributeOptions?.find(
                                option => option.label === newAdvancedMapping[attribute],
                              ) || ''
                            }
                            onChange={option => {
                              handleChange(option, attribute);
                            }}
                            placeholder="Select FSA attribute"
                          />
                          <span className="ml-3">
                            <OverlayTrigger overlay={<Tooltip>Delete</Tooltip>}>
                              <FontAwesomeIcon
                                className={pointerOnHover}
                                icon={faTrashAlt}
                                onClick={() => {
                                  const newMapping = { ...newAdvancedMapping };
                                  const newFlags = { ...removeFlags };
                                  delete newMapping[attribute];
                                  delete newFlags[attribute];
                                  setNewAdvancedMapping(newMapping);
                                  setRemoveFlags(newFlags);
                                }}
                              />
                            </OverlayTrigger>
                          </span>
                        </Col>
                      </Row>
                      {attributeOptions?.find(
                        option => option.label === newAdvancedMapping[attribute],
                      )?.inputType === AttributeInputType.MULTI_VALUE && (
                        <Row className="mt-2">
                          <Col>
                            <Form.Check
                              type="checkbox"
                              label="Remove matched value from list instead of adding it"
                              checked={removeFlags[attribute] || false}
                              onChange={e => {
                                setRemoveFlags({
                                  ...removeFlags,
                                  [attribute]: e.target.checked,
                                });
                              }}
                            />
                          </Col>
                        </Row>
                      )}
                    </div>
                  );
                })}
            </Col>
          </Row>

          <Row className="mt-3">
            <Col>
              {!isNewMappingsAreValid && (
                <div className={css(styles.redText)}>
                  Please fill all the fields to insert the mapping
                </div>
              )}
            </Col>
            <Col className="d-flex justify-content-end">
              <Button
                disabled={!isNewMappingsAreValid}
                variant="outline-primary"
                onClick={() => {
                  const mappingsToInsert = Object.keys(newAdvancedMapping).reduce((acc, key) => {
                    const finalKey = removeFlags[key] ? `REMOVE_VALUE(${key})` : key;
                    acc[finalKey] = newAdvancedMapping[key];
                    return acc;
                  }, {});
                  const commit = () => {
                    const updatedMap = { ...unsavedAttributeMap, ...mappingsToInsert };
                    if (
                      originalAttribute &&
                      !Object.keys(mappingsToInsert).includes(originalAttribute)
                    ) {
                      delete updatedMap[originalAttribute];
                    }
                    setUnsavedAttributeMap(updatedMap);
                    onHide();
                  };
                  // Guard against silently overwriting a different existing mapping when the
                  // source-attribute key is renamed
                  // (or a new mapping collides with an existing one).
                  const collidingKey = Object.keys(mappingsToInsert).find(
                    key =>
                      key !== originalAttribute && Object.keys(unsavedAttributeMap).includes(key),
                  );
                  if (collidingKey) {
                    areYouSure(
                      commit,
                      `The source attribute "${collidingKey}" is already mapped. ` +
                        'Continuing will overwrite the existing mapping.',
                    );
                  } else {
                    commit();
                  }
                }}
              >
                {originalAttribute ? 'Update' : 'Insert'}
              </Button>
            </Col>
            <Col md="auto" className="d-flex justify-content-end">
              <Button variant="outline-secondary" onClick={onHide}>
                Close
              </Button>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
    </Modal>
  );
}

const styles = StyleSheet.create({
  smallText: {
    fontSize: '0.8rem',
  },
  mappingSection: {
    border: `1px solid ${neutralGray}`,
    padding: '1rem',
    borderRadius: '5px',
    marginBottom: '1rem',
  },
  redText: {
    color: 'red',
    fontSize: '0.8rem',
  },
});

AdvancedAttributeMappingModal.propTypes = {
  onHide: PropTypes.func.isRequired,
  attributes: PropTypes.object.isRequired,
  attributeMap: PropTypes.object.isRequired,
  unsavedAttributeMap: PropTypes.object.isRequired,
  setUnsavedAttributeMap: PropTypes.func.isRequired,
  attributesToFilter: PropTypes.array,
  editingAttribute: PropTypes.string,
  editingValue: PropTypes.string,
};

export default AdvancedAttributeMappingModal;
